import { Text } from "@certn/cascadia/foundations/typography/Text";
import { css, cx } from "@emotion/css";
import { Key } from "react-aria-components";
import { useTranslation } from "react-i18next";

import { ColumnDefinition } from "./DataTable";
import { getTextStyle } from "../../foundations/typography/styles";

interface Props<T> {
  columns: ColumnDefinition<T>[];
  rows: T[];
  rowKey?: string;
  testId?: string;
  minWidth?: string;
}

export function EditableDataTable<T extends { id: Key }>(props: Props<T>) {
  const { t } = useTranslation();
  const {
    columns,
    rows,
    rowKey = "id",
    testId = "editable-data-table",
    minWidth = "934px",
  } = props;

  return (
    <div className={styles.scrollContainer} data-testid={testId}>
      <table className={cx(styles.table, css({ minWidth }))}>
        <thead>
          <tr>
            {columns.map((col) => (
              <th
                key={col.key}
                className={cx(
                  styles.th,
                  col.displayRightBorder && styles.borderRight,
                  col.width ? css({ width: col.width }) : undefined
                )}
              >
                <div className={styles.thContent}>
                  {col.name ?? col.textValue ?? col.key}
                </div>
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.length === 0 ? (
            <tr>
              <td colSpan={columns.length} className={styles.empty}>
                <Text
                  style="global/body-secondary"
                  align="center"
                  color="text-weak"
                >
                  {t("No results found.")}
                </Text>
              </td>
            </tr>
          ) : (
            rows.map((item) => (
              <tr key={item[rowKey as keyof typeof item] as Key}>
                {columns.map((col) => {
                  const CellTag = col.isRowHeader ? "th" : "td";
                  return (
                    <CellTag
                      key={col.key}
                      scope={col.isRowHeader ? "row" : undefined}
                      className={cx(
                        styles.td,
                        col.displayRightBorder && styles.borderRight
                      )}
                    >
                      {col.render
                        ? col.render(item)
                        : String(item[col.key as keyof typeof item] ?? "")}
                    </CellTag>
                  );
                })}
              </tr>
            ))
          )}
        </tbody>
      </table>
    </div>
  );
}

// Styles must match DataTable's Column.tsx and Cell.tsx — see ParityWithDataTable story
const styles = {
  scrollContainer: css({
    width: "100%",
    overflowX: "auto",
  }),
  table: css(
    {
      width: "100%",
      tableLayout: "fixed",
      borderCollapse: "collapse",
      boxSizing: "border-box",
      "th, td": { boxSizing: "border-box" },
      "tbody tr": {
        borderBottomWidth: 1,
        borderBottomStyle: "solid",
      },
      "thead tr": {
        borderBottomWidth: 2,
        borderBottomStyle: "solid",
      },
      "tr, th": { borderColor: "var(--border-base)" },
    },
    getTextStyle("form/body-secondary")
  ),
  th: css(
    {
      paddingTop: "var(--space-2-5)",
      paddingBottom: "calc(var(--space-2-5) - 2px)",
      paddingInline: "var(--space-3) var(--space-05)",
      textAlign: "left",
    },
    getTextStyle("global/button-medium")
  ),
  thContent: css({
    display: "inline-flex",
    alignItems: "center",
    borderRadius: "var(--border-radius-2)",
    paddingBlock: "var(--space-1)",
    paddingInline: "var(--space-05)",
  }),
  td: css({
    paddingTop: "var(--space-4)",
    paddingBottom: "calc(var(--space-4) - 1px)",
    paddingInline: "var(--space-3)",
    overflow: "hidden",
    textAlign: "left",
  }),
  borderRight: css({
    borderRight: "1px solid var(--border-base)",
  }),
  empty: css({
    padding: "var(--space-6)",
    minHeight: 288,
    textAlign: "center",
  }),
};
